import { useState } from 'react';
import {
  type PromoEventRow,
  useCancelPromoEventMutation,
  useCreatePromoEventMutation,
  usePromoEventsQuery,
} from '../api/moderation.js';

const PERK_OPTIONS = [
  'who_liked_you',
  'who_visited_you',
  'advanced_filters',
  'read_receipts',
  'weekly_boost',
  'priority_moderation',
] as const;

export function PromotionalEventsRoute() {
  const events = usePromoEventsQuery();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display font-semibold text-2xl text-neutral-900">Promotional events</h2>
        <button
          type="button"
          onClick={() => setShowCreate((s) => !s)}
          className="rounded-md bg-primary-600 px-4 py-2 font-medium text-sm text-white hover:bg-primary-700"
        >
          {showCreate ? 'Cancel' : '+ New event'}
        </button>
      </div>
      <p className="mb-6 text-neutral-600 text-sm">
        Admin-triggered free-for-all grants. ad_free is always stripped (subscribers keep their
        ad-free perk; non-subscribers continue to see ads). Max 168h, max 3 concurrent, max 8 per
        quarter.
      </p>

      {showCreate ? <CreateForm onDone={() => setShowCreate(false)} /> : null}

      <section className="mt-6">
        {events.isLoading ? (
          <p className="text-neutral-500 text-sm">Loading…</p>
        ) : events.isError ? (
          <p className="text-danger-700 text-sm">Couldn't load events.</p>
        ) : (events.data?.events ?? []).length === 0 ? (
          <p className="text-neutral-500 text-sm">No events yet.</p>
        ) : (
          <ul className="space-y-3">
            {events.data?.events.map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function EventCard({ event }: { event: PromoEventRow }) {
  const cancel = useCancelPromoEventMutation();
  return (
    <li className="rounded-md border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-neutral-900">{event.name}</p>
          <p className="mt-1 text-neutral-500 text-xs">
            {event.scheduleType.replace('_', ' ')} · {event.segmentType} ·{' '}
            <span className="capitalize">{event.status}</span>
          </p>
          {event.utcStartsAt ? (
            <p className="mt-0.5 text-neutral-500 text-xs">
              {new Date(event.utcStartsAt).toLocaleString()} →{' '}
              {event.utcEndsAt ? new Date(event.utcEndsAt).toLocaleString() : '?'}
            </p>
          ) : event.localDate ? (
            <p className="mt-0.5 text-neutral-500 text-xs">
              {event.localDate} (local) for {event.durationHours}h
            </p>
          ) : null}
          <p className="mt-2 text-neutral-700 text-xs">Perks: {event.perks.join(', ') || '—'}</p>
        </div>
        {['scheduled', 'active', 'partially_active'].includes(event.status) ? (
          <button
            type="button"
            onClick={() => cancel.mutate({ eventId: event.id, revokeImmediately: false })}
            disabled={cancel.isPending}
            className="rounded-md border border-danger-300 bg-white px-3 py-1.5 font-medium text-danger-700 text-xs hover:bg-danger-50 disabled:opacity-60"
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel'}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const create = useCreatePromoEventMutation();
  const [name, setName] = useState('');
  const [scheduleType, setScheduleType] = useState<'utc_window' | 'local_time_window'>(
    'utc_window',
  );
  const [utcStartsAt, setUtcStartsAt] = useState('');
  const [utcEndsAt, setUtcEndsAt] = useState('');
  const [localDate, setLocalDate] = useState('');
  const [localStartTime, setLocalStartTime] = useState('20:00');
  const [durationHours, setDurationHours] = useState('12');
  const [segmentType, setSegmentType] = useState<'global' | 'countries'>('global');
  const [segmentCountries, setSegmentCountries] = useState('');
  const [perks, setPerks] = useState<string[]>(['who_liked_you']);
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = () => {
    setErrors([]);
    const body: Parameters<typeof create.mutate>[0] = {
      name,
      perks,
      scheduleType,
      segmentType,
      ...(scheduleType === 'utc_window'
        ? {
            utcStartsAt: new Date(utcStartsAt).toISOString(),
            utcEndsAt: new Date(utcEndsAt).toISOString(),
          }
        : {
            localDate,
            localStartTime,
            durationHours: Number.parseInt(durationHours, 10),
          }),
      ...(segmentType === 'countries'
        ? {
            segmentCountries: segmentCountries
              .split(',')
              .map((c) => c.trim().toUpperCase())
              .filter((c) => c.length === 3),
          }
        : {}),
    };
    create.mutate(body, {
      onSuccess: () => onDone(),
      onError: (err) => {
        const apiErr = err as { body?: { errors?: { code: string; message: string }[] } };
        const errs = apiErr.body?.errors;
        if (Array.isArray(errs)) {
          setErrors(errs.map((e) => `${e.code}: ${e.message}`));
        } else {
          setErrors([String(err)]);
        }
      },
    });
  };

  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 p-4">
      <h3 className="mb-3 font-medium text-primary-900">New promotional event</h3>
      <div className="space-y-3">
        <label className="block">
          <span className="text-neutral-700 text-sm">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
            placeholder="Launch week — all features unlocked"
          />
        </label>
        <label className="block">
          <span className="text-neutral-700 text-sm">Schedule</span>
          <select
            value={scheduleType}
            onChange={(e) => setScheduleType(e.target.value as 'utc_window' | 'local_time_window')}
            className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
          >
            <option value="utc_window">UTC window (global instant)</option>
            <option value="local_time_window">Local-time window (rolls by TZ)</option>
          </select>
        </label>
        {scheduleType === 'utc_window' ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-neutral-700 text-xs">Starts at (UTC)</span>
              <input
                type="datetime-local"
                value={utcStartsAt}
                onChange={(e) => setUtcStartsAt(e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-neutral-700 text-xs">Ends at (UTC)</span>
              <input
                type="datetime-local"
                value={utcEndsAt}
                onChange={(e) => setUtcEndsAt(e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              />
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-neutral-700 text-xs">Local date</span>
              <input
                type="date"
                value={localDate}
                onChange={(e) => setLocalDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-neutral-700 text-xs">Start time</span>
              <input
                type="time"
                value={localStartTime}
                onChange={(e) => setLocalStartTime(e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-neutral-700 text-xs">Duration (hours)</span>
              <input
                type="number"
                min={1}
                max={168}
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              />
            </label>
          </div>
        )}
        <label className="block">
          <span className="text-neutral-700 text-sm">Segment</span>
          <select
            value={segmentType}
            onChange={(e) => setSegmentType(e.target.value as 'global' | 'countries')}
            className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
          >
            <option value="global">Global (all launch countries)</option>
            <option value="countries">Specific countries</option>
          </select>
        </label>
        {segmentType === 'countries' ? (
          <label className="block">
            <span className="text-neutral-700 text-xs">
              Country codes (comma-separated ISO-3, e.g. PAK,IND,BGD)
            </span>
            <input
              type="text"
              value={segmentCountries}
              onChange={(e) => setSegmentCountries(e.target.value)}
              className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
            />
          </label>
        ) : null}
        <fieldset className="block">
          <legend className="text-neutral-700 text-sm">Perks granted</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {PERK_OPTIONS.map((p) => (
              <label key={p} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={perks.includes(p)}
                  onChange={(e) => {
                    setPerks((prev) =>
                      e.target.checked ? [...prev, p] : prev.filter((x) => x !== p),
                    );
                  }}
                />
                {p.replace(/_/g, ' ')}
              </label>
            ))}
          </div>
          <p className="mt-1 text-neutral-500 text-xs">
            ad_free is unselectable — only paid subscriptions remove ads.
          </p>
        </fieldset>
        {errors.length > 0 ? (
          <ul className="rounded-md bg-danger-50 p-2 text-danger-700 text-xs">
            {errors.map((e) => (
              <li key={e}>· {e}</li>
            ))}
          </ul>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending || !name}
            className="rounded-md bg-primary-600 px-4 py-2 font-medium text-sm text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
          <button
            type="button"
            onClick={onDone}
            className="rounded-md border border-neutral-200 bg-white px-4 py-2 font-medium text-neutral-700 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
